import { StandardTextFieldProps, TextField } from '@mui/material';
import { CountrySelect } from 'components/CountrySelect';
import moment from 'moment';
import React, { ChangeEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Dac7FormErrorType, IPrivatePersonData, PrivatePersonDac7FormField, privatePersonStrictlyRequiredFields } from 'types/user.types';
import { camelToKebabCase } from 'utils/format-text';

interface IPrivatePersonAccountTypeFormProps {
  handleData: ({ target }: ChangeEvent<HTMLInputElement> | SyntheticEvent, valueRaw?: string, fieldName?: string) => void;
  errors: Dac7FormErrorType;
  data: IPrivatePersonData;
}

export const PrivatePersonAccountTypeForm = ({ handleData, errors, data }: IPrivatePersonAccountTypeFormProps) => {
  const { t } = useTranslation();

  const commonTextFieldProps: StandardTextFieldProps = {
    variant: 'standard',
    className: 'DAC7-form-field',
    InputLabelProps: {
      shrink: true,
    },
    onChange: handleData,
  };

  const getCustomTextFieldProps = (fieldName: keyof IPrivatePersonData) => {
    const fieldNameInKebabCase = camelToKebabCase(fieldName);
    const isEstoniaSelected = fieldName === PrivatePersonDac7FormField.idCode && data.residencyCountry === 'EE';

    return {
      id: fieldNameInKebabCase,
      name: fieldNameInKebabCase,
      error: errors && !!errors[fieldName],
      inputProps: {
        'data-field': fieldName,
      },
      label: t(`pages.DAC7.form.private-person.${fieldNameInKebabCase}`),
      value: data[fieldName] || '',
      helperText: errors && t(errors[fieldName]),
      required: isEstoniaSelected ? true : privatePersonStrictlyRequiredFields.includes(fieldName),
      placeholder: t(`pages.DAC7.form.private-person.placeholder-${fieldNameInKebabCase}`),
    };
  };

  return (
    <div className="DAC7-form__private-person-form DAC7-user-data-form">
      <h5 className="DAC7-form__private-person-form-title">{t('pages.DAC7.form.private-person.title')}</h5>
      <TextField {...commonTextFieldProps} {...getCustomTextFieldProps(PrivatePersonDac7FormField.fullName)} />
      <TextField
        {...commonTextFieldProps}
        {...getCustomTextFieldProps(PrivatePersonDac7FormField.dateOfBirth)}
        type="date"
        inputProps={{
          'data-field': PrivatePersonDac7FormField.dateOfBirth,
          max: moment().format('YYYY-MM-DD'),
        }}
        className="pseudo-select"
      />

      <TextField {...commonTextFieldProps} {...getCustomTextFieldProps(PrivatePersonDac7FormField.residencyAddress)} />

      <CountrySelect
        classes="DAC7-form-field"
        error={errors && !!errors[PrivatePersonDac7FormField.residencyCountry]}
        helperText={errors && t(errors[PrivatePersonDac7FormField.residencyCountry])}
        countryCode={data[PrivatePersonDac7FormField.residencyCountry]}
        label={t(`pages.DAC7.form.private-person.${camelToKebabCase(PrivatePersonDac7FormField.residencyCountry)}`)}
        handleChange={(_, item) => handleData(_ as ChangeEvent, item?.code || '', PrivatePersonDac7FormField.residencyCountry)}
      />

      <TextField {...commonTextFieldProps} {...getCustomTextFieldProps(PrivatePersonDac7FormField.idCode)} />

      <TextField {...commonTextFieldProps} {...getCustomTextFieldProps(PrivatePersonDac7FormField.vat)} />
    </div>
  );
};
